import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Geometry } from 'geojson';
import { Observable } from 'rxjs';

export type ZoneExpertZoneType = 'customer' | 'carrier';
export type ZoneExpertSourceType = 'postal_codes' | 'manual' | 'imported';
export type ZoneExpertReferenceBoundaryType = 'place' | 'urban_area' | 'metro' | 'municipality';

export interface ZoneExpertApiResponse<T> {
  ok: boolean;
  success?: boolean;
  resource?: string;
  data: T;
  warnings?: string[];
  errors?: string[];
  message?: string;
  valid?: boolean;
  meta?: Record<string, unknown>;
}

export interface ZoneExpertCleanupOptions {
  min_hole_square_miles: number;
  simplify_tolerance_degrees: number;
}

export interface ZoneExpertGeometryReport {
  raw_geometry_type?: string | null;
  cleaned_geometry_type?: string | null;
  raw_valid?: boolean | null;
  raw_valid_reason?: string | null;
  cleaned_valid?: boolean | null;
  cleaned_valid_reason?: string | null;
  raw_point_count?: number | null;
  cleaned_point_count?: number | null;
  raw_part_count?: number | null;
  cleaned_part_count?: number | null;
  hole_count?: number | null;
  filled_hole_count?: number | null;
  kept_hole_count?: number | null;
  raw_square_miles?: number | null;
  cleaned_square_miles?: number | null;
  area_delta_square_miles?: number | null;
  area_delta_pct?: number | null;
  cleanup?: ZoneExpertCleanupOptions;
}

export interface ZoneExpertOverlap {
  zone_uuid: string;
  name: string;
  zone_id?: string | null;
  country?: string | null;
  active?: boolean | null;
  overlap_square_miles?: number | null;
  overlap_pct_of_candidate?: number | null;
  overlap_pct_of_zone?: number | null;
}

export interface ZoneExpertPreviewData {
  source_type: ZoneExpertSourceType;
  boundary_geojson: Geometry;
  square_miles: number | null;
  center_lat: number | null;
  center_lng: number | null;
  geometry: ZoneExpertGeometryReport;
  postal_codes?: {
    input_count: number;
    matched_count: number;
    missing: string[];
  };
  overlaps: ZoneExpertOverlap[];
}

export interface ZoneExpertZone {
  zone_uuid: string;
  zone_type: ZoneExpertZoneType;
  contact_uuid: string;
  name: string;
  zone_name?: string;
  zone_id?: string | null;
  country: string;
  active: boolean;
  description?: string | null;
  source_type?: ZoneExpertSourceType | null;
  boundary_geojson?: Geometry | null;
  square_miles?: number | null;
  center_lat?: number | null;
  center_lng?: number | null;
  postal_codes?: string[];
}

export interface ZoneExpertReferenceBoundary {
  reference_boundary_uuid: string;
  boundary_type: ZoneExpertReferenceBoundaryType;
  country_code: string;
  source: string;
  source_year: number;
  source_id: string;
  name: string;
  display_name: string;
  region_code?: string | null;
  region_name?: string | null;
  simplification?: string | null;
  point_count: number;
  square_miles?: number | null;
  center_lat?: number | null;
  center_lng?: number | null;
  bbox?: number[] | null;
  metadata?: Record<string, unknown> | null;
  boundary_geojson?: Geometry | null;
}

export interface ZoneExpertPreviewRequest {
  zone_type: ZoneExpertZoneType;
  contact_uuid?: string;
  zone_uuid?: string;
  country: string;
  source_type: ZoneExpertSourceType;
  boundary_geojson: Geometry;
  cleanup: ZoneExpertCleanupOptions;
}

export interface ZoneExpertSaveRequest extends ZoneExpertPreviewRequest {
  name: string;
  zone_id: string | null;
  description?: string | null;
  active: boolean;
  sync_postal_codes: boolean;
}

@Injectable({ providedIn: 'root' })
export class ZoneMakerExpertApiService {
  private readonly apiUrl = '/api/zone_maker_expert.php';
  private readonly referenceBoundaryApiUrl = '/api/reference_boundaries.php';

  constructor(private readonly http: HttpClient) {}


  searchReferenceBoundaries(
    query: string,
    country = 'USA',
    boundaryType: ZoneExpertReferenceBoundaryType = 'place',
    limit = 25
  ): Observable<ZoneExpertApiResponse<ZoneExpertReferenceBoundary[]>> {
    const params = new HttpParams()
      .set('action', 'search')
      .set('boundary_type', boundaryType)
      .set('country', country)
      .set('q', query)
      .set('limit', String(limit));

    return this.http.get<ZoneExpertApiResponse<ZoneExpertReferenceBoundary[]>>(this.referenceBoundaryApiUrl, { params });
  }


  bboxReferenceBoundaries(
    bounds: { minLat: number; minLng: number; maxLat: number; maxLng: number },
    country = 'USA',
    boundaryType: ZoneExpertReferenceBoundaryType = 'place',
    limit = 150
  ): Observable<ZoneExpertApiResponse<ZoneExpertReferenceBoundary[]>> {
    const params = new HttpParams()
      .set('action', 'bbox')
      .set('boundary_type', boundaryType)
      .set('country', country)
      .set('include_geometry', 'true')
      .set('min_lat', String(bounds.minLat))
      .set('min_lng', String(bounds.minLng))
      .set('max_lat', String(bounds.maxLat))
      .set('max_lng', String(bounds.maxLng))
      .set('limit', String(limit));

    return this.http.get<ZoneExpertApiResponse<ZoneExpertReferenceBoundary[]>>(this.referenceBoundaryApiUrl, { params });
  }

  getReferenceBoundary(boundaryUuid: string): Observable<ZoneExpertApiResponse<ZoneExpertReferenceBoundary>> {
    const params = new HttpParams()
      .set('action', 'boundary')
      .set('boundary_uuid', boundaryUuid);

    return this.http.get<ZoneExpertApiResponse<ZoneExpertReferenceBoundary>>(this.referenceBoundaryApiUrl, { params });
  }

  capabilities(): Observable<ZoneExpertApiResponse<Record<string, unknown>>> {
    const params = new HttpParams().set('action', 'capabilities');
    return this.http.get<ZoneExpertApiResponse<Record<string, unknown>>>(this.apiUrl, { params });
  }

  listZones(zoneType: ZoneExpertZoneType, contactUuid: string, includeGeometry = false): Observable<ZoneExpertApiResponse<ZoneExpertZone[]>> {
    const params = new HttpParams()
      .set('action', 'zones')
      .set('zone_type', zoneType)
      .set('contact_uuid', contactUuid)
      .set('include_geometry', includeGeometry ? 'true' : 'false')
      .set('include_postal_codes', 'false')
      .set('limit', '500');

    return this.http.get<ZoneExpertApiResponse<ZoneExpertZone[]>>(this.apiUrl, { params });
  }

  getZone(zoneType: ZoneExpertZoneType, zoneUuid: string, contactUuid?: string): Observable<ZoneExpertApiResponse<ZoneExpertZone>> {
    let params = new HttpParams()
      .set('action', 'zone')
      .set('zone_type', zoneType)
      .set('zone_uuid', zoneUuid);

    if (contactUuid) {
      params = params.set('contact_uuid', contactUuid);
    }

    return this.http.get<ZoneExpertApiResponse<ZoneExpertZone>>(this.apiUrl, { params });
  }

  preview(request: ZoneExpertPreviewRequest): Observable<ZoneExpertApiResponse<ZoneExpertPreviewData>> {
    return this.http.post<ZoneExpertApiResponse<ZoneExpertPreviewData>>(this.apiUrl, {
      action: 'preview',
      ...request
    });
  }

  validate(request: ZoneExpertPreviewRequest): Observable<ZoneExpertApiResponse<{ geometry: ZoneExpertGeometryReport; overlaps: ZoneExpertOverlap[] }>> {
    return this.http.post<ZoneExpertApiResponse<{ geometry: ZoneExpertGeometryReport; overlaps: ZoneExpertOverlap[] }>>(this.apiUrl, {
      action: 'validate',
      ...request
    });
  }

  create(request: ZoneExpertSaveRequest): Observable<ZoneExpertApiResponse<ZoneExpertZone>> {
    return this.http.post<ZoneExpertApiResponse<ZoneExpertZone>>(this.apiUrl, {
      action: 'create',
      ...request
    });
  }

  update(zoneUuid: string, request: ZoneExpertSaveRequest): Observable<ZoneExpertApiResponse<ZoneExpertZone>> {
    return this.http.post<ZoneExpertApiResponse<ZoneExpertZone>>(this.apiUrl, {
      action: 'update',
      zone_uuid: zoneUuid,
      ...request
    });
  }

  derivePostalCodes(zoneType: ZoneExpertZoneType, zoneUuid: string, persist = false): Observable<ZoneExpertApiResponse<{ postal_codes: string[] }>> {
    return this.http.post<ZoneExpertApiResponse<{ postal_codes: string[] }>>(this.apiUrl, {
      action: 'derive_postal_codes',
      zone_uuid: zoneUuid,
      zone_type: zoneType,
      method: 'center',
      persist
    });
  }
}
